import React, { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  TextField,
} from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  convertTo12Hour,
  convertTo24Hour,
  getCurrentTime12,
  getCurrentTime24,
} from "../../../utils/timeUtils";

const styles = {
  dialogPaper: {
    borderRadius: "16px",
    padding: "24px",
    maxWidth: "400px",
    width: "100%",
    boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.1)",
  },
  dialogContainer: { display: "flex", flexDirection: "column", gap: 3 },
  headerText: { color: "#F57C00", fontWeight: "bold", textAlign: "center" },
  employeeContainer: { display: "flex", alignItems: "center", gap: 2 },
  avatar: { width: 56, height: 56, bgcolor: "#bdbdbd" },
  employeeName: { fontWeight: "bold", color: "#333" },
  employeeDate: { color: "#757575" },
  shiftDetailsBox: {
    bgcolor: "#FFF8E1",
    borderRadius: "8px",
    p: 2,
    border: "1px solid #FFE082",
  },
  shiftDetailsTitle: {
    color: "#757575",
    fontWeight: 600,
    display: "block",
    mb: 1,
  },
  shiftDetailsContent: { display: "flex", flexDirection: "column", gap: 1 },
  shiftRow: { display: "flex", justifyContent: "space-between" },
  shiftLabel: { color: "#757575" },
  shiftValueOrange: { color: "#F57C00", fontWeight: 600 },
  shiftValueDark: { color: "#333", fontWeight: 600 },
  inputLabel: { color: "#757575", mb: 0.5, display: "block" },
  inputContainer: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #e0e0e0",
    pb: 0.5,
  },
  nativeInput: (disabled) => ({
    border: "none",
    outline: "none",
    fontSize: "1rem",
    width: "100%",
    color: "#333",
    fontFamily: "inherit",
    backgroundColor: disabled ? "transparent" : undefined,
    cursor: disabled ? "not-allowed" : undefined,
  }),
  inputIcon: { color: "#F57C00", fontSize: 20 },
  checkoutDisabledText: {
    color: "#9e9e9e",
    fontSize: "0.7rem",
    mt: 1,
    display: "block",
  },
  errorText: {
    color: "#d32f2f",
    fontSize: "0.7rem",
    mt: 0.5,
    display: "block",
  },
  actionsBox: { display: "flex", gap: 2 },
  cancelButton: {
    borderColor: "#e0e0e0",
    color: "#757575",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "8px",
    py: 1.5,
    "&:hover": {
      borderColor: "#bdbdbd",
      bgcolor: "#f5f5f5",
    },
  },
  submitButton: (isCompleted) => ({
    background: isCompleted
      ? "#bdbdbd"
      : "linear-gradient(90deg, #FFB74D 0%, #FF9800 100%)",
    color: "white",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "8px",
    py: 1.5,
    boxShadow: "none",
    "&:hover": {
      boxShadow: isCompleted ? "none" : "0 4px 12px rgba(245, 124, 0, 0.3)",
      background: isCompleted
        ? "#bdbdbd"
        : "linear-gradient(90deg, #FFB74D 0%, #FF9800 100%)",
    },
  }),
};
const AttendancePopup = ({ open, onClose, data, onSubmit }) => {
  const { t } = useTranslation();
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [reason, setReason] = useState("");
  const [isCheckoutEnabled, setIsCheckoutEnabled] = useState(false);
  const [showReasonField, setShowReasonField] = useState(false);
  const isOngoing =
    data?.status === "ongoing" ||
    (data?.check_in_time && !data?.check_out_time);
  const isCompleted = data?.status === "completed";
  useEffect(() => {
    if (data) {
      const currentTime12 = getCurrentTime12();
      if (!isOngoing && !isCompleted) {
        setCheckIn(currentTime12);
        setCheckOut("");
        setShowReasonField(false);
        setReason("");
        setIsCheckoutEnabled(false);
      } else {
        setCheckIn(convertTo12Hour(data.check_in_time) || "");
        setCheckOut(currentTime12);
        setReason("");
        const endTime = data.shift?.end_time || data.end_time;
        if (endTime) {
          const endTime24 =
            endTime.includes(":") && !endTime.includes("M")
              ? endTime
              : convertTo24Hour(endTime);
          const [endHour, endMin] = endTime24.split(":").map(Number);
          const shiftDate = new Date(data.date);
          const shiftEndTime = new Date(shiftDate);
          shiftEndTime.setHours(endHour, endMin, 0, 0);
          const thirtyMinsBefore = new Date(
            shiftEndTime.getTime() - 30 * 60 * 1000
          );
          const now = new Date();
          now.setSeconds(0, 0);
          thirtyMinsBefore.setSeconds(0, 0);
          const isEarlyCheckout = now < thirtyMinsBefore;
          setShowReasonField(isEarlyCheckout);
          if (!isEarlyCheckout) {
            setReason("");
          }
          setIsCheckoutEnabled(true);
        }
      }
    }
  }, [data, isOngoing, isCompleted]);
  if (!data) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: styles.dialogPaper,
      }}
    >
      <Box sx={styles.dialogContainer}>
        <Typography variant="h6" sx={styles.headerText}>
          {t("layout.attendance.add_attendance")}
        </Typography>
        <Box sx={styles.employeeContainer}>
          <Avatar src={data.employee?.profile_pic} sx={styles.avatar} />
          <Box>
            <Typography variant="subtitle1" sx={styles.employeeName}>
              {data.employee?.name || t("pages.shift_management.employee_name")}
            </Typography>
            <Typography variant="caption" sx={styles.employeeDate}>
              {data.date ? format(new Date(data.date), "dd MMM, yyyy") : ""}
            </Typography>
          </Box>
        </Box>
        {(data.shift?.shift_type || data.shift_type) && (
          <Box sx={styles.shiftDetailsBox}>
            <Typography variant="caption" sx={styles.shiftDetailsTitle}>
              {t("layout.attendance.assigned_shift_details")}
            </Typography>
            <Box sx={styles.shiftDetailsContent}>
              <Box sx={styles.shiftRow}>
                <Typography variant="body2" sx={styles.shiftLabel}>
                  {t("layout.attendance.shift_type")}
                </Typography>
                <Typography variant="body2" sx={styles.shiftValueOrange}>
                  {data.shift?.shift_type || data.shift_type}
                </Typography>
              </Box>
              <Box sx={styles.shiftRow}>
                <Typography variant="body2" sx={styles.shiftLabel}>
                  {t("layout.attendance.shift_start_time")}
                </Typography>
                <Typography variant="body2" sx={styles.shiftValueDark}>
                  {convertTo12Hour(data.shift?.start_time || data.start_time) ||
                    "--:--"}
                </Typography>
              </Box>
              <Box sx={styles.shiftRow}>
                <Typography variant="body2" sx={styles.shiftLabel}>
                  {t("layout.attendance.shift_end_time")}
                </Typography>
                <Typography variant="body2" sx={styles.shiftValueDark}>
                  {convertTo12Hour(data.shift?.end_time || data.end_time) ||
                    "--:--"}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        <Box>
          <Typography variant="caption" sx={styles.inputLabel}>
            {t("layout.attendance.check_in")}
          </Typography>
          <Box sx={styles.inputContainer}>
            <input
              type="text"
              value={checkIn}
              readOnly
              disabled
              style={styles.nativeInput(true)}
            />
            <AccessTime sx={styles.inputIcon} />
          </Box>
        </Box>
        {(isOngoing || isCompleted) && (
          <Box>
            <Typography variant="caption" sx={styles.inputLabel}>
              {t("layout.attendance.check_out")}
            </Typography>
            <Box sx={styles.inputContainer}>
              <input
                type="text"
                value={checkOut}
                readOnly
                disabled
                style={styles.nativeInput(true)}
                placeholder="--:-- --"
              />
              <AccessTime sx={styles.inputIcon} />
            </Box>
            {isOngoing && (
              <Typography variant="caption" sx={styles.checkoutDisabledText}>
                {t("layout.attendance.checkout_disabled_msg")}
              </Typography>
            )}
          </Box>
        )}
        {showReasonField && (
          <Box>
            <Typography variant="caption" sx={styles.inputLabel}>
              {t("layout.attendance.early_checkout_reason")}
            </Typography>
            <Box sx={styles.inputContainer}>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("layout.attendance.enter_early_checkout_reason")}
                style={styles.nativeInput(false)}
              />
            </Box>
            <Typography variant="caption" sx={styles.errorText}>
              {t("layout.attendance.reason_required")}
            </Typography>
          </Box>
        )}
        <Box sx={styles.actionsBox}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            sx={styles.cancelButton}
          >
            {t("common.cancel").toUpperCase()}
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() =>
              onSubmit({
                ...data,
                check_in_time: convertTo24Hour(checkIn),
                check_out_time: isOngoing ? convertTo24Hour(checkOut) : null,
                reason,
                status: isOngoing ? "completed" : "ongoing",
              })
            }
            disabled={isCompleted || (showReasonField && !reason)}
            sx={styles.submitButton(isCompleted)}
          >
            {isCompleted
              ? t("layout.attendance.completed")
              : t("layout.attendance.submit")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AttendancePopup;
